import React from 'react';
import { CheckOutlined, EyeOutlined, PauseOutlined, StopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  ORDER_PROCESS_STEPS,
  canOpenOrderProcessStep,
  getOrderProcessStepCopyKey,
  getOrderProcessStepIndex,
  type OrderProcessCopyFacts,
  type OrderProcessPosition,
  type OrderProcessStepKey,
} from './orderProcessSteps';

interface OrderProcessRailProps {
  position: OrderProcessPosition;
  viewed: OrderProcessStepKey;
  /** INVOICED: the last step is done, not in progress. */
  closed: boolean;
  onView: (key: OrderProcessStepKey) => void;
  /** Wording facts (self-pickup, all in stock, confirmation sent). */
  copy?: Partial<OrderProcessCopyFacts>;
  /** A new, unsaved order: no step after Szkic opens yet. */
  lockAhead?: boolean;
}

/**
 * Clickable rail of the eight ZK steps with the step banner (makieta `head()`
 * second card). A finished step opens as a read-only preview; Wstrzymane /
 * Anulowane keep the last step and say so.
 */
export default function OrderProcessRail({ position, viewed, closed, onView, copy = {}, lockAhead = false }: OrderProcessRailProps) {
  const { t } = useTranslation(['orders']);
  const viewedIndex = getOrderProcessStepIndex(viewed);
  const label = (key: OrderProcessStepKey) => t(getOrderProcessStepCopyKey(key, 'label', copy));

  let banner: React.ReactNode = null;
  if (viewedIndex < position.currentIndex) {
    banner = (
      <div className="zk-banner zk-banner--readonly" data-testid="zk-step-banner" data-readonly="true">
        <EyeOutlined aria-hidden="true" />
        <span className="zk-banner__text">
          {t('orders:process.rail.readOnly', { step: label(viewed), current: label(position.current) })}
        </span>
      </div>
    );
  } else if (position.interruptedBy && viewed === position.current) {
    banner = (
      <div className="zk-banner zk-banner--warning" data-testid="zk-step-banner">
        {position.interruptedBy === 'ON_HOLD' ? <PauseOutlined aria-hidden="true" /> : <StopOutlined aria-hidden="true" />}
        <span className="zk-banner__text">
          {t(`orders:process.interrupted.${position.interruptedBy}`, { step: label(position.current) })}
        </span>
      </div>
    );
  }
  // No info banner on the current step: the tip and „Wykonuje: …” are gone from every step (owner 2026-10-05).

  return (
    <section className="zk-section zk-rail-card" aria-label={t('orders:process.rail.ariaLabel')}>
      <div className="zk-rail-wrap">
        <ol className="zk-rail" data-viewing-label={label(viewed)}>
          {ORDER_PROCESS_STEPS.map((step, index) => {
            const done = index < position.currentIndex || (closed && index === position.currentIndex);
            const current = index === position.currentIndex && !done;
            const open = canOpenOrderProcessStep(position, step.key) && !(lockAhead && index > position.currentIndex);
            const classes = [
              'zk-rail__item',
              done ? 'is-done' : '',
              current ? 'is-current' : '',
              current && position.interruptedBy ? 'is-interrupted' : '',
              step.key === viewed ? 'is-viewing' : '',
            ].filter(Boolean).join(' ');
            return (
              <li key={step.key} className={classes}>
                <button
                  type="button"
                  className="zk-rail__button"
                  disabled={!open}
                  aria-current={current ? 'step' : undefined}
                  aria-pressed={step.key === viewed}
                  aria-label={open ? label(step.key) : t('orders:process.rail.locked', { step: label(step.key) })}
                  onClick={() => onView(step.key)}
                  data-testid={`zk-rail-step-${step.key}`}
                >
                  <span className="zk-rail__dot" aria-hidden="true">{done ? <CheckOutlined /> : index + 1}</span>
                  <span className="zk-rail__label">{label(step.key)}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
      {banner}
    </section>
  );
}
